// Trainee Badges — earned badges in full color + locked ones grayed with "points away".
// Driven by Course + Quiz points only (Arcade excluded).
import React, { useCallback, useEffect, useState } from 'react';
import { api, mediaUrl } from '@/lib/api';
import { motion } from 'framer-motion';
import { Award, Lock, Sparkles, Trophy } from 'lucide-react';
import BadgeQuizModal from '@/components/BadgeQuizModal';

const colorClass = (c) => ({
    gold: 'from-amber-500 to-orange-500',
    emerald: 'from-emerald-500 to-teal-600',
    blue: 'from-blue-500 to-indigo-600',
    violet: 'from-violet-500 to-fuchsia-500',
    rose: 'from-rose-500 to-pink-500',
    slate: 'from-slate-600 to-slate-800',
}[c] || 'from-amber-500 to-orange-500');

export default function TraineeBadges({ embedded = false }) {
    const [data, setData] = useState(null);
    const [quizBadge, setQuizBadge] = useState(null);

    const load = useCallback(() => {
        api.get('/trainee/badge-progress')
            .then((r) => setData(r.data))
            .catch(() => setData({ points_total: 0, badges: [] }));
    }, []);

    useEffect(() => { load(); }, [load]);

    if (!data) {
        return <div className={`${embedded ? '' : 'max-w-2xl mx-auto px-4'} py-16 text-center text-white/60`}>Loading badges…</div>;
    }

    const earned = data.badges.filter((b) => b.earned);
    const unlockable = data.badges.filter((b) => b.unlockable);
    const locked = data.badges.filter((b) => !b.earned && !b.unlockable);

    return (
        <div className={`${embedded ? '' : 'max-w-2xl mx-auto px-4 py-5'} space-y-6`} data-testid="trainee-badges-page">
            {/* Header */}
            <div className="rounded-3xl bg-brand-navy text-white p-6 relative overflow-hidden">
                <div className="absolute -right-6 -top-6 opacity-10"><Trophy className="w-32 h-32" /></div>
                <div className="text-brand-yellow/80 text-xs font-bold uppercase tracking-wider">Your Badges</div>
                <div className="flex items-end gap-2 mt-1">
                    <span className="text-4xl font-black text-brand-yellow" data-testid="badge-points-total">{data.points_total}</span>
                    <span className="text-white/70 text-sm mb-1">course &amp; quiz points</span>
                </div>
                <div className="text-white/60 text-xs mt-2">{earned.length} of {data.badges.length} badges earned · Arcade points don&apos;t count toward badges</div>
            </div>

            {/* Ready to unlock (milestone reached, quiz pending) */}
            {unlockable.length > 0 && (
                <section data-testid="unlockable-section">
                    <h2 className="font-display font-bold text-lg text-white mb-3 flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-brand-yellow" /> Ready to unlock ({unlockable.length})
                    </h2>
                    <div className="space-y-3">
                        {unlockable.map((b) => (
                            <div key={b.id} className="bg-white rounded-2xl border-2 border-brand-yellow/60 p-4 flex items-center gap-4 shadow-sm" data-testid={`unlockable-badge-${b.id}`}>
                                {b.image_url
                                    ? <img src={mediaUrl(b.image_url)} alt={b.name} className="h-14 w-14 rounded-2xl object-cover shrink-0" />
                                    : <div className={`h-14 w-14 rounded-2xl grid place-items-center font-display font-black text-white text-xl shrink-0 bg-gradient-to-br ${colorClass(b.color)}`}>{b.name?.[0]}</div>}
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold text-brand-black text-sm truncate">{b.name}</div>
                                    <div className="text-xs text-emerald-600 font-bold">Milestone reached! Pass the quiz ({b.pass_percent}%) to earn it.</div>
                                </div>
                                <button onClick={() => setQuizBadge(b)} data-testid={`take-quiz-${b.id}`}
                                    className="shrink-0 px-4 py-2 rounded-xl bg-brand-yellow text-brand-black font-bold text-sm hover:brightness-105">
                                    Take quiz
                                </button>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Earned */}
            {earned.length > 0 && (
                <section>
                    <h2 className="font-display font-bold text-lg text-white mb-3 flex items-center gap-2">
                        <Award className="w-5 h-5 text-brand-yellow" /> Earned ({earned.length})
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {earned.map((b, i) => (
                            <motion.div key={b.id} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.05 }}
                                className="bg-white rounded-2xl border border-brand-navy/5 p-4 text-center shadow-sm" data-testid={`earned-badge-${b.id}`}>
                                {b.image_url
                                    ? <img src={mediaUrl(b.image_url)} alt={b.name} className="h-16 w-16 rounded-2xl object-cover mx-auto" />
                                    : <div className={`h-16 w-16 rounded-2xl grid place-items-center font-display font-black text-white text-2xl mx-auto bg-gradient-to-br ${colorClass(b.color)}`}>{b.name?.[0]}</div>}
                                <div className="font-bold text-brand-black text-sm mt-2 truncate">{b.name}</div>
                                <div className="text-brand-yellow text-xs font-extrabold">{b.points_required} pts</div>
                            </motion.div>
                        ))}
                    </div>
                </section>
            )}

            {/* Locked */}
            {locked.length > 0 && (
                <section>
                    <h2 className="font-display font-bold text-lg text-white mb-3 flex items-center gap-2">
                        <Lock className="w-5 h-5 text-brand-navy/40" /> Locked ({locked.length})
                    </h2>
                    <div className="space-y-3">
                        {locked.map((b) => (
                            <div key={b.id} className="bg-white rounded-2xl border border-brand-navy/5 p-4 flex items-center gap-4" data-testid={`locked-badge-${b.id}`}>
                                <div className="h-14 w-14 rounded-2xl grid place-items-center bg-slate-100 text-slate-300 grayscale shrink-0">
                                    {b.image_url
                                        ? <img src={mediaUrl(b.image_url)} alt="" className="h-full w-full rounded-2xl object-cover opacity-40" />
                                        : <Award className="w-7 h-7" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold text-brand-navy/70 text-sm truncate">{b.name}</div>
                                    <div className="text-xs font-bold text-amber-600 flex items-center gap-1" data-testid={`badge-away-${b.id}`}>
                                        <Sparkles className="w-3 h-3" /> {b.points_away} pts away{b.has_quiz ? ' · quiz to unlock' : ''}
                                    </div>
                                    <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
                                        <div className={`h-full rounded-full bg-gradient-to-r ${colorClass(b.color)}`} style={{ width: `${b.progress_pct}%` }} />
                                    </div>
                                </div>
                                <div className="text-right shrink-0">
                                    <div className="text-brand-black font-black text-sm">{b.points_required}</div>
                                    <div className="text-[10px] text-brand-navy/40 uppercase">pts</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {data.badges.length === 0 && (
                <div className="text-center text-white/60 py-12">
                    <Award className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    No badges available yet. Check back soon!
                </div>
            )}

            {quizBadge && (
                <BadgeQuizModal
                    badgeId={quizBadge.id}
                    onClose={() => setQuizBadge(null)}
                    onEarned={load}
                />
            )}
        </div>
    );
}
